import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import getFlowConfig, { getApplicationType } from './flowConfig'
import t from '../../../translations/main'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import storage from 'src/components/Core/Utils/storage'

import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import OnboardingWrapper from './OnboardingWrapper'
import FormWrapper from './FormWrapper'
import StepProgressBar from 'src/components/Molecules/StepProgressBar/StepProgressBar'
import { MainContentWrapper } from './Onboarding.styles'

import { DropdownOptions, FormConfig, GetApplicationsType, LoadDropdownProps, StepsStatusType } from './types'
import {
  ProgressListStateType,
  OptionsListProps,
} from 'src/components/Molecules/StepProgressBar/types'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import { DropdownOption, useApplicationSelected, useDropdownOptions, useProspectsApplications, useProspectsApplicationsUpdate } from 'src/components/Core/Context/OnboardingContext'
import { checkMode } from 'src/components/Organisms/Form/utils'

export const USER_ROLES = ['Employee', 'Client', 'Prospect', 'Hold', 'Rejected']
const USER_PERMISSIONS = ['administrator', 'read-only', 'user', 'documentation']

export const stringToNumber = (string: string) => {
  const result = Number(string || '1')
  return isNaN(result) ? 0 : result
}

const createOptionsList = (
  flowKey: string,
  flowConfig: FormConfig[],
  stepsStatus: StepsStatusType[]
): OptionsListProps[] => {
  const getState = (index: number): ProgressListStateType => {
    const state = stepsStatus?.[`${index}`]?.status

    if (state) return state
    return 'warning'
  }

  return flowConfig?.map((formObj: FormConfig, index: number) => ({
    id: index,
    label: formObj.key === 'SignatureForm' ? t('onboarding.forms.signature.steps') : t(`onboarding.forms.${flowKey}.${formObj?.key || ''}.steps`),
    state: getState(index),
    unreadCommentsCount: stepsStatus?.[`${index}`]?.unreadCommentsCount,
    commentsCount: stepsStatus?.[`${index}`]?.commentsCount
  }))
}

const checkWarning = (optionsList: OptionsListProps[]) => (
  optionsList?.map(obj => obj?.state).includes('warning')
)

const setAppInfoOnLocalStorage = (appInfo: GetApplicationsType) => {
  storage.set(storage?.keys?.onboarding?.applicationId, appInfo.applicationId)
  storage.set(storage?.keys?.onboarding?.prospectPermission, appInfo.permission.replace(appInfo.permission.substring(0,1),appInfo.permission.substring(0,1).toUpperCase()))
  storage.set(storage?.keys?.onboarding?.applicationStatus, appInfo.status)
  storage.set(storage?.keys?.onboarding?.applicationResubmitted, appInfo.stepsStatus.some(step => step.commentsCount > 0) || '')
}

const isApplicationStructureInvalid = (appInfo: GetApplicationsType, userRole: string) => {
  if(
    !appInfo ||
    !appInfo.applicationId ||
    !appInfo.applicationType ||
    appInfo.progressIndex === undefined ||
    !appInfo.stepsStatus ||
    !Array.isArray(appInfo.stepsStatus) ||
    appInfo.stepsStatus.length === 0 ||
    !USER_ROLES.includes(userRole) ||
    !USER_PERMISSIONS.includes(appInfo.permission.toLowerCase())
  ) return true

  const accountFlowType = getApplicationType(appInfo.applicationType)
  const flowConfig = getFlowConfig(accountFlowType)
  if(flowConfig.length !== appInfo.stepsStatus.length || appInfo.progressIndex > flowConfig.length) return true
}

const loadDropdownOptions = ({sendRequest, setDropdownOptions}: LoadDropdownProps) => {
  const modifyResponse = (data: DropdownOptions) => {
    const countryTelephoneCodes: DropdownOption[] = data.countries.map(item => {
      return {
        label: item.displayPhoneCode.replace('(','').replace(')',''),
        index: item.itemIndex
      }
    })
    const countries: DropdownOption[]= data.countries.map(item => {
      return {
        label: item.country,
        index: item.itemIndex
      }
    })
    const currencies: DropdownOption[]= data.currencies.map(item => {
      return {
        label: item.currency,
        index: item.itemIndex
      }
    })
    setDropdownOptions({currencies, countries, countryTelephoneCodes})
  }

  sendRequest(
    {
      scope: 'onboarding',
      url: import.meta.env.VITE_MOCK_DATA !== 'true'
        ? `${getBaseApiHost()}/AccountOpeningForms/getdropdownoptions`
        : `${window.location.origin}/mock/dropdownOptions.json`,
      method: 'GET',
    },
    modifyResponse
  )
}

const checkError = ({data, selectedApplication, userRole}: {data: GetApplicationsType[], selectedApplication: number, userRole: string}) => {
  if (data?.length === 0 || typeof data === 'string') {
    return '/hold'
  }
  // @ts-ignore:next-line
  if(data?.reasonPhrase){
    // @ts-ignore:next-line
    if(JSON.parse(data.reasonPhrase)?.[0]?.key === 'generalError'){
      return '/onboarding/noapplications'
    } else {
      return '/error/internalProblem'
    }
  }
  if(isApplicationStructureInvalid(data?.[`${selectedApplication}`], userRole)) {
    return '/error/internalProblem'
  }
  return ''
}

const Onboarding = () => {
  const navigate = useNavigate()
  const { flowKey, step } = useParams()
  const currentStep = stringToNumber(step) - 1
  const flowConfig = getFlowConfig(flowKey) ||  []
  const [optionsList, setOptionsList] = useState([])
  const [successFlag, setSuccessFlag] = useState(false)
  const [completedStepList, setCompletedStepList] = useState(
    flowConfig?.map(() => false)
  )
  const [refetch, setRefetch] = useState(false)

  const selectedApplication = useApplicationSelected()
  const selectedApplicationUpdate = useProspectsApplicationsUpdate()
  const prospectsApplications = useProspectsApplications()
  const applicationType = prospectsApplications[`${selectedApplication}`]?.applicationType || ''

  const { isLoading, sendRequest, error } = useFetch()
  const { setDropdownOptions } = useDropdownOptions()

  // eslint-disable-next-line
  const updateStatus = ({ originStatus, destinationStep }: { originStatus: string, destinationStep: number }) => {
    if (
      completedStepList?.includes(false)
      && currentStep !== destinationStep
      && prospectsApplications[`${selectedApplication}`]?.permission !== 'documentation'
    ) {
      const params = `applicationId=${storage.get(storage?.keys?.onboarding?.applicationId)}&stepOriginIndex=${currentStep + 1}&stepOriginStatus=${['complete', 'warning'].includes(originStatus) ? originStatus : 'not_started'}&stepDestinationIndex=${destinationStep + 1}&stepDestinationStatus=in_progress`
      sendRequest(
        {
          scope: 'onboarding',
          url: import.meta.env.VITE_MOCK_DATA !== 'true'
            ? `${getBaseApiHost()}/${applicationType}/update${applicationType}StepStatus?${params}`
            : `${window.location.origin}/mock/getApp.json`,
          method: 'POST',
        },
        () => null
      )
    }
  }

  useEffect(() => {
    // eslint-disable-next-line
    const setFetchedDataHandler = (data: GetApplicationsType[]) => {
      const userRole = storage.get(storage?.keys?.main?.user)?.idTokenClaims?.extension_UserStatus

      const hasError = checkError({data, selectedApplication, userRole})
      if(hasError){
        navigate(hasError)
        return
      }

      const appInfo = data?.[`${selectedApplication as number}`]
      const accountFlowType = getApplicationType(appInfo.applicationType)
      const flowConfig = getFlowConfig(accountFlowType)

      selectedApplicationUpdate(data)
      setCompletedStepList(appInfo.stepsStatus.map((step: StepsStatusType) => step.status === 'complete'))
      setOptionsList(
        createOptionsList(
          accountFlowType,
          flowConfig,
          appInfo.stepsStatus
        )
      )
      loadDropdownOptions({sendRequest, setDropdownOptions})
      setAppInfoOnLocalStorage(appInfo)

      if(appInfo.permission === 'documentation' && prospectsApplications.length === 0){
        navigate('/onboarding/applications-dashboard')
      } else {
        navigate(`/onboarding/${accountFlowType}/${appInfo.progressIndex || 1}`, { replace: window.location.pathname.includes('loading') })
      }
    }

    // GET
    sendRequest(
      {
        scope: 'onboarding',
        url: import.meta.env.VITE_MOCK_DATA !== 'true'
          ? `${getBaseApiHost()}/AccountOpeningForms/getapplications`
          : `${window.location.origin}/mock/getApp.json`,
        method: 'GET',
      },
      setFetchedDataHandler
    )

  }, [selectedApplication, refetch])

  if (completedStepList && !completedStepList.includes(false) && !successFlag) {
    setSuccessFlag(true)
  }

  const nextStepHandler = (submittedSuccessfully?: boolean) => {
    setOptionsList(prevList => (
      prevList?.map((obj, index) => {
        if (index === currentStep) {
          obj.state = 'complete'
        }
        return obj
      })
    ))

    setCompletedStepList(prevList => (
      prevList?.map((value, index) => (index === currentStep ? true : value))
    ))

    // when the last form is submitted ok then there is no need to call the updaStatus function because the step was sent in the submit endpoint
    // and if the step is as warning then the flow for that is in the more below block
    !submittedSuccessfully &&
      storage.get(storage?.keys?.onboarding?.stepCompleted) !== 'warning' &&
      updateStatus({
        destinationStep: currentStep < flowConfig.length - 2 ? currentStep + 1 : (optionsList.findIndex(step => (step.state === 'in_progress' || step.state === 'not_started'))),
        originStatus: 'complete'
      })
    if(submittedSuccessfully) return
    // flowConfig length for Corp is 25, currentStep is zero based, for instance Authorized Signatories is step number 24 but with currentStep 23
    // so when currentStep is 23 it means that we are at the last Corp step before Signature, this if block is only for the last step
    if(currentStep === flowConfig.length - 2 && !storage.get(storage?.keys?.onboarding?.applicationResubmitted)){ // condition here means: last step + first time of application (NOT submitted)
      navigate(`/onboarding/${flowKey}/${optionsList.findIndex(step => (step.state === 'in_progress' || step.state === 'not_started')) + 1}`)
      return
    }
    if (currentStep !== flowConfig.length - 1 || optionsList.findIndex(step => step.state === 'warning') > -1){
      if(storage.get(storage?.keys?.onboarding?.applicationStatus) === 'in_progress' && storage.get(storage?.keys?.onboarding?.applicationResubmitted)) { // condition here means: application RE-submitted
        let nextWarningIndex = optionsList.findIndex((step, index) => step.state === 'warning' && index > currentStep)
        if(nextWarningIndex === -1) { nextWarningIndex = optionsList.findIndex(step => step.state === 'warning') }
        if(nextWarningIndex === -1) {
          navigate('/error/internalProblem')
          return
        }
        updateStatus({
          destinationStep: nextWarningIndex,
          originStatus: 'complete'
        })
        navigate(`/onboarding/${flowKey}/${nextWarningIndex + 1}`)
        return
      }
      navigate(`/onboarding/${flowKey}/${stringToNumber(step) + 1}`)
    }
  }

  const prevStepHandler = () => {
    if (currentStep === 0) return

    updateStatus({ originStatus: optionsList[`${currentStep}`].state, destinationStep: currentStep - 1 })

    navigate(`/onboarding/${flowKey}/${stringToNumber(step) - 1}`)
  }

  const onListClick = (index: number) => {
    if (index !== currentStep) {
      !successFlag && updateStatus({ originStatus: optionsList[`${currentStep}`].state, destinationStep: index })
      navigate(`/onboarding/${flowKey}/${index + 1}`)
    }
  }

  if (error) {
    navigate('/error/internalProblem')
    return
  }

  const loading = isLoading || !step || step === '0' || !storage.get(storage?.keys?.onboarding?.applicationId) || optionsList.length === 0
  return (
    loading ? <Spinner removeFullscreen /> :
      <OnboardingWrapper
        currentStep={currentStep}
        stepsTotal={flowConfig?.length}
        isDone={successFlag}
        isWarning={checkWarning(optionsList)}
        completedStepList={completedStepList}
        stepName={flowConfig[`${parseInt(step) - 1}`].key}
        applicationType={applicationType}
        setOptionsList={setOptionsList}
        setRefetch={setRefetch}
      >

        <MainContentWrapper
          show={!loading}
          alignItems='flex-start'
          disableButtons={checkMode('readOnly', optionsList, currentStep)
           || checkMode('stepsReadOnly', optionsList, currentStep)
          }
        >
          <StepProgressBar
            optionsList={optionsList}
            onListClick={index => onListClick(index)}
            currentStep={currentStep}
            progressIndex={prospectsApplications[`${selectedApplication}`].progressIndex}
            applicationStatus={prospectsApplications[`${selectedApplication}`].status}
          />
          <FormWrapper
            flowConfig={flowConfig}
            currentStep={currentStep}
            prevStep={prevStepHandler}
            nextStep={nextStepHandler}
            applicationId={storage.get(storage?.keys?.onboarding?.applicationId)}
            isLastForm={completedStepList.slice(0,completedStepList.length).reduce((acc, item) => {
              if(item === true) { return ++acc}
              return acc
            }, 0) === flowConfig.length - 2
              ? completedStepList.slice(0,completedStepList.length).findIndex(item => item === false) === currentStep
                ? true
                : false
              : false
            }
            setCompletedStepList={setCompletedStepList}
            setOptionsList={setOptionsList}
            applicationType={applicationType}
            optionsList={optionsList}
          />
        </MainContentWrapper>
      </OnboardingWrapper>
  )
}

export default Onboarding
